import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import _sortBy from 'lodash/sortBy';
import { media } from '@minecraft.themes';
import Row from '@minecraft.row';
import Col from '@minecraft.col';
import { gqlWillingnessEnum as willingness } from '@minecraft.graphql-types';
import { SCREEN_NAMES, EditProfile, AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { AnchorButton } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';

const commonNoSectionI18Label = 'common:form.label.noSelectionYet';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

/* ----- Types ----- */
type EditLinkProps = {
  isAdmin: boolean;
  onEditClick: () => void;
  href: string;
  qaId: string;
  label?: string;
};

/* ----- Helpers ----- */
const sectionName = 'role-preferences';

/* ----- Styled Components ----- */
const StyledRow = styled(Row)`
  margin-bottom: 1rem;
  border-bottom: 0;
  position: relative;
  ${media.sm`
    margin-bottom: 0.5rem;
    border-bottom: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
  `}
`;

const StyledColLbl = styled(Col)`
  font-size: 0.85rem;
  font-weight: 600;
  padding-left: 0;
`;

const StyledColValue = styled(Col)`
  font-size: 0.85rem;
  padding-left: 0;
  ${media.sm`
    padding-left: 0.9375rem;
  `}
`;

const FadedValue = styled.div`
  color: ${(props): string => props.theme.colors.primary.grey2};
`;

const RolePrefContainer = styled.div`
  background: ${(props): string => props.theme.colors.primary.white};
  padding: 1rem;
  ${media.sm`
    padding: 0.9375rem 3rem;
  `}
`;

const RecordsContainer = styled.div`
  padding-left: 1.25rem;
`;

const RoleWrapper = styled.div`
  background: ${(props): string => props.theme.colors.accent.ltGrey2};
  margin: 0 -1rem;
  padding: 1rem;
  ${media.sm`
    padding: 1.25rem 2rem;
    margin: 0 -2rem;
  `}
`;

const InfoHeading = styled.h3`
  font-size: 1.7rem;
  line-height: 2rem;
  margin-bottom: 0.5rem;
`;

const EditColumn = styled(Col)`
  text-align: right;
  position: absolute;
  right: 0;
  ${media.sm`
    position: static;
  `}
`;

const VerticalList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

/* ----- Help Components ----- */
const EditLink = ({ isAdmin, href, qaId, onEditClick, label }: EditLinkProps): JSX.Element => {
  const { t } = useTranslation();

  if (isAdmin) {
    return (
      <AnchorButton data-qa-id={qaId} onClick={onEditClick} aria-label={label}>
        {t('common:button.edit')}
      </AnchorButton>
    );
  }

  return (
    <DuoLink data-qa-id={qaId} href={href} aria-label={label}>
      {t('common:button.edit')}
    </DuoLink>
  );
};

const ExtrasWorkInfo = ({ playableAge }): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div>
      {playableAge
        ? t('common:profile.rolePreferences.interestedExtrasAge', { playableAge })
        : t('common:profile.rolePreferences.interestedExtras')}
    </div>
  );
};

/* ----- Main Component ----- */
const RolePreferences: FC = (): JSX.Element => {
  const { t, formatIntlDate } = useTranslation();

  const {
    fetcher: { profile },
    appType,
    isAdmin,
    onEditClick,
    isPreview,
  } = React.useContext(ProfileViewContext);

  if (isPreview) {
    return null;
  }

  const hasWork = profile.pursuing && profile.pursuing.length > 0;
  const hasExtraWork =
    profile.willingness && profile.willingness.filter((e) => e.code === willingness.EXTRAS_WORK).length > 0;
  const otherWillingness = profile.willingness
    ? profile.willingness.filter((e) => e.code !== willingness.EXTRAS_WORK)
    : null;
  const talentUnions = profile.talentUnions
    ? profile.talentUnions.filter((tu) => !profile.talentUnions.find((cu) => cu.parentObject?.id === tu.id))
    : [];

  return (
    <RoleWrapper data-testid="profile-role-prefs-container">
      <RolePrefContainer id="div-role-preferences">
        <InfoHeading>{t('common:profile.nav.rolePreferences')}</InfoHeading>
        <EditProfile.CompoundAppearanceInfo data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}`}>
          {appType === AppType.Reps ? t('common:profile.appearanceInfoReps') : t('common:profile.appearanceInfo')}
        </EditProfile.CompoundAppearanceInfo>

        <RecordsContainer>
          <StyledRow data-testid="role-pref-work-types">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-types-of-work`}
              sm="3"
              xs="12"
            >
              {t('common:form.title.typesOfWork')}
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-types-of-work`} sm="7" xs="12">
              {hasWork ? (
                <div>{profile.pursuing.map((e) => e.name).join(', ')}</div>
              ) : (
                <FadedValue>{t(commonNoSectionI18Label)}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/work-types`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-types-of-work-edit`}
                onEditClick={(): void => onEditClick('work-types')}
                label={t('common:form.title.typesOfWork')}
              />
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="role-pref-extras-work">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-extras-work`} sm="3" xs="12">
              {t('common:profile.rolePreferences.extrasWork')}:
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-extras-work`} sm="7" xs="12">
              {hasExtraWork ? (
                <ExtrasWorkInfo playableAge={profile.playableAge} />
              ) : (
                <div>{t('common:form.label.no')}</div>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/work-types`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-extras-work-edit`}
                onEditClick={(): void => onEditClick('work-types')}
                label={t('common:profile.rolePreferences.extrasWork')}
              />
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="role-pref-work-locations">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-working-locations`}
              sm="3"
              xs="12"
            >
              {t('form.title.workingLocations')}:
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-working-locations`} sm="7" xs="12">
              {profile.workingLocations && profile.workingLocations.length > 0 ? (
                <div>
                  {_sortBy(profile.workingLocations, (w) => w.sortOrder).map((e) => (
                    <React.Fragment key={e.id}>
                      {e.name} {e.sortOrder === 0 && ` ${t('common:profile.info.primary')}`}
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <FadedValue>{t(commonNoSectionI18Label)}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/working-locations`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-working-locations-edit`}
                onEditClick={(): void => onEditClick('working-locations')}
                label={t('form.title.workingLocations')}
              />
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="role-pref-unions">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-union-affiliations`}
              sm="3"
              xs="12"
            >
              {t('common:profile.rolePreferences.unionAffiliations')}:
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-union-affiliations`}
              sm="7"
              xs="12"
            >
              {talentUnions.length > 0 ? (
                <VerticalList>
                  {talentUnions.map((union) => (
                    <li key={union.id}>
                      {union.parentObject ? `${union.parentObject.name} ${union.name}` : union.name}
                    </li>
                  ))}
                </VerticalList>
              ) : (
                <FadedValue>{t(commonNoSectionI18Label)}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/unions`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-union-affiliations-edit`}
                onEditClick={(): void => onEditClick('unions')}
                label={t('common:profile.rolePreferences.unionAffiliations')}
              />
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="role-pref-documents">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-documents`} sm="3" xs="12">
              {t('common:form.title.documents')}:
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-documents`} sm="7" xs="12">
              {profile.documentTypes && profile.documentTypes.length > 0 ? (
                <div>{profile.documentTypes.map((e) => e.name).join(', ')}</div>
              ) : (
                <FadedValue>{t(commonNoSectionI18Label)}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/documents`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-documents-edit`}
                onEditClick={(): void => onEditClick('documents')}
                label={t('common:form.title.documents')}
              />
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="role-pref-willingness">
            <StyledColLbl data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-willingness`} sm="3" xs="12">
              {t('common:form.title.willingness')}:
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-willingness`} sm="7" xs="12">
              {otherWillingness?.length > 0 ? (
                <div>{otherWillingness.map((e) => e.name).join(', ')}</div>
              ) : (
                <FadedValue>{t(commonNoSectionI18Label)}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/willingness`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-willingness-edit`}
                onEditClick={(): void => onEditClick('willingness')}
                label={t('common:form.title.willingness')}
              />
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="role-pref-professions">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-other-profession`}
              sm="3"
              xs="12"
            >
              {t('common:form.title.otherProfessions')}:
            </StyledColLbl>
            <StyledColValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-other-profession`} sm="7" xs="12">
              {profile.profession ? (
                <div>{profile.profession}</div>
              ) : (
                <FadedValue>{t(commonNoSectionI18Label)}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/other-professions`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-other-profession-edit`}
                onEditClick={(): void => onEditClick('other-professions')}
                label={t('common:form.title.otherProfessions')}
              />
            </EditColumn>
          </StyledRow>
          <StyledRow data-testid="role-pref-pregnancy">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-pregnancy-due-date`}
              sm="3"
              xs="12"
            >
              {t('common:form.title.pregnancyDueDate')}:
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-pregnancy-due-date`}
              sm="7"
              xs="12"
            >
              {profile.pregnancy ? (
                <div>{formatIntlDate({ date: profile.pregnancy, format: 'MONTH_DAY_YEAR' })}</div>
              ) : (
                <FadedValue>{t(commonNoSectionI18Label)}</FadedValue>
              )}
            </StyledColValue>
            <EditColumn sm="2">
              <EditLink
                isAdmin={isAdmin}
                href={`/profile/${profile.profileId}/pregnancy`}
                qaId={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-pregnancy-due-date-edit`}
                onEditClick={(): void => onEditClick('pregnancy')}
                label={t('common:form.title.pregnancyDueDate')}
              />
            </EditColumn>
          </StyledRow>
        </RecordsContainer>
      </RolePrefContainer>
    </RoleWrapper>
  );
};

export default RolePreferences;
